import {Box, Button, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle} from "@mui/material";
import './CreateDocument.scss';
import React, {useEffect, useState} from "react";
import CreateDocumentStep1 from "./CreateDocumentStep1";
import CreateDocumentStep2 from "./CreateDocumentStep2";
import CreateDocumentStep3 from "./CreateDocumentStep3";
import {useLocation} from "react-router-dom";
import {DocumentDraft, useDocumentDraft} from "./useDocumentDraft";
import {DOCUMENT_TYPE_CODE_PISMO_OGOLNE} from "./DocumentData";
import {useKeycloak} from "@react-keycloak/web";


interface GeneratedDocument {
    signature: string;
    receiverName: string;
    receiverAddress: string;
    inbox: boolean;
    emailFilled: boolean;
    sending: boolean;
    sent: boolean;
    sendingWithEmail: boolean;
}

export default function CreateDocument() {

    const location = useLocation();
    const keycloak = useKeycloak();
    const {loadDraft, clearDraft} = useDocumentDraft(keycloak.keycloak.subject ?? '');
    const isReplyOrForward = location.state?.replyTo || location.state?.forwardFrom;

    const [step, setStep] = useState(isReplyOrForward ? 2 : 1)
    const [documentTypeCode, setDocumentTypeCode] = useState<Number>(isReplyOrForward ? DOCUMENT_TYPE_CODE_PISMO_OGOLNE : 0)
    const [signature, setSignature] = useState("")
    const [generatedDocuments, setGeneratedDocuments] = useState<GeneratedDocument[]>([])
    const [restoredDraft, setRestoredDraft] = useState<DocumentDraft | null>(null);
    const [draftDialogOpen, setDraftDialogOpen] = useState(false);

    useEffect(() => {
        if (isReplyOrForward) {
            setDocumentTypeCode(DOCUMENT_TYPE_CODE_PISMO_OGOLNE);
            setStep(2);
            return;
        }

        const draft = loadDraft();
        if (!draft) return;

        setRestoredDraft(draft);
        setDraftDialogOpen(true);
    }, [location.state]);

    const handleDraftRestore = () => {
        if (restoredDraft) {
            setDocumentTypeCode(restoredDraft.documentTypeCode);
            setStep(2);
        }
        setDraftDialogOpen(false);
    };

    const handleDraftReject = () => {
        clearDraft();
        setRestoredDraft(null);
        setDraftDialogOpen(false);
    };

    let step1Callback = (code: Number) => {
        if (code != null) {
            setDocumentTypeCode(code);
            setStep(2);
        }
    }

    let step2Callback = (documents: GeneratedDocument[]) => {
        if (documents != null) {
            setGeneratedDocuments(documents);
            setStep(3);
        }
    }

    const handleDocumentChange = (signature: string, newDoc: GeneratedDocument) => {
        setGeneratedDocuments(prevDocuments =>
            prevDocuments.map(doc =>
                doc.signature === signature
                    ? newDoc
                    : doc
            )
        );
    };

    return (
        <>
            <Dialog open={draftDialogOpen}>
                <DialogTitle>Wersja robocza</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        Podczas poprzedniej sesji przerwano proces tworzenia dokumentu. Czy chcesz przywrócić zapisaną wersję?
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleDraftRestore} variant="contained">Tak</Button>
                    <Button onClick={handleDraftReject} sx={{'&.MuiButton-root': {background: '#878787', '&:hover': {background: '#656565'}}, color: '#fff'}}>Nie</Button>
                </DialogActions>
            </Dialog>
            <Box className="GenerateDocument">
                {step == 1 && <CreateDocumentStep1 callback={step1Callback}/>}
                {step == 2 && (
                    <CreateDocumentStep2
                        documentTypeId={documentTypeCode}
                        callback={step2Callback}
                        initialReplyTo={location.state?.replyTo}
                        initialForwardFrom={location.state?.forwardFrom}
                        initialDraftFields={restoredDraft?.fields}
                        initialDraftRecipients={restoredDraft?.recipients}
                    />
                )}
                {step == 3 && <CreateDocumentStep3
                    documents={generatedDocuments}
                    onDocumentChange={handleDocumentChange}
                />}
            </Box>
        </>
    )
}
